import { Box, Button, Skeleton, Typography } from "@mui/material";
import { observer } from "mobx-react";
import { FC } from "react";

import { useChatStore } from "../../hooks/useChatStore";
import StatusChip from "./components/table-components/StatusChip";
import { Claim, ClaimStatus } from "../../types/claims";
import EyeIcon from "../icons/EyeIcon";
import Row from "../ui/core/Row";
import ChevronLeftIcon from "../icons/ChevronLeftIcon";
import { useClaimsStore } from "../../hooks/useClaimsStore";

interface ClaimSummaryBarProps {
  loading?: boolean;
  claimId?: string;
}

/**
 * Fixed claim summary bar shown above the chat input for claims chats (when the panel is closed).
 * "Claim Details" triggers the same action as ClaimPanelOpenRail's "View Claim".
 */
const ClaimSummaryBar: FC<ClaimSummaryBarProps> = observer(
  ({ loading, claimId }) => {
    const chatStore = useChatStore();
    const { claimToPreview: claimToPreviewStore } = useClaimsStore();

    const claimIdFromChat =
      (chatStore.chatDetails as { claim_id?: string } | null)?.claim_id ??
      (chatStore.chatDetails as { claim_number?: string } | null)
        ?.claim_number ??
      null;

    // The number this bar is *about*: always the current chat's claim.
    const claimNumberFromChat =
      chatStore.chatDetails?.claim_number ?? claimIdFromChat ?? claimId ?? null;

    // claimsStore.claimToPreview and chatStore.claimDetailData are global and
    // outlive the chat that set them (leaving /claims or switching chats does
    // not always clear them), so a claim previewed elsewhere can still be in
    // there. Only merge a source that is the same claim as this chat's,
    // otherwise the bar would show another claim's patient/payer/status next to
    // this chat's number — and open that other claim (REV-2244).
    const isSameClaim = (candidate?: Claim | null) =>
      !!candidate &&
      (!claimNumberFromChat ||
        candidate.claimNumber === claimNumberFromChat ||
        candidate.id === claimIdFromChat);

    const detailClaim = chatStore.claimDetailData?.claim as Claim | undefined;

    const claimToPreview = {
      ...(chatStore.chatDetails?.claim?.claim || {}),
      ...(isSameClaim(detailClaim) ? detailClaim : {}),
      ...(isSameClaim(claimToPreviewStore) ? claimToPreviewStore : {}),
      // The chat is the source of truth for the identity, so display and
      // navigation can never diverge.
      ...(claimNumberFromChat ? { claimNumber: claimNumberFromChat } : {}),
    } as Claim;

    const handleOpenClaimDetails = () => {
      const targetClaimId =
        claimIdFromChat || claimToPreview?.claimNumber || claimId;
      if (!targetClaimId) return;

      chatStore.setIsClaimDataLoading(true);
      chatStore.setClaimIdToPreview(targetClaimId);
      void chatStore.loadClaimDetail(targetClaimId);
    };

    // ClaimPreview (the side panel) already shows this claim's details when open
    if (chatStore.claimIdToPreview) {
      return null;
    }

    return (
      <Box
        sx={{
          px: 2,
          py: 1,
          width: "100%",
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "white",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Row
            sx={{
              alignItems: "center",
              gap: 1,
              flex: 1,
              minWidth: 0,
              flexWrap: "nowrap",
            }}
          >
            {claimToPreview.claimNumber ? (
              <>
                <Typography fontWeight={600} noWrap sx={{ flexShrink: 0 }}>
                  {claimToPreview.claimNumber}
                </Typography>

                {claimToPreview?.patientName && (
                  <>
                    <Box
                      component="span"
                      sx={{ color: "text.disabled", flexShrink: 0 }}
                    >
                      ·
                    </Box>
                    <Typography
                      noWrap
                      color="text.secondary"
                      sx={{
                        fontSize: "0.875rem",
                        flexShrink: 1,
                        minWidth: 50,
                        maxWidth: 200,
                      }}
                    >
                      {claimToPreview.patientName}
                    </Typography>
                  </>
                )}

                {claimToPreview?.payer && (
                  <>
                    <Box
                      component="span"
                      sx={{ color: "text.disabled", flexShrink: 0 }}
                    >
                      ·
                    </Box>
                    <Typography
                      noWrap
                      color="text.secondary"
                      sx={{
                        fontSize: "0.875rem",
                        minWidth: 50,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {claimToPreview.payer}
                    </Typography>
                  </>
                )}

                {claimToPreview?.status ? (
                  <StatusChip
                    dotted
                    status={claimToPreview.status as ClaimStatus}
                    sx={{
                      flexShrink: 0,
                      borderRadius: "50px",
                    }}
                  />
                ) : null}
              </>
            ) : null}
          </Row>

          <Button
            variant="contained"
            color="primary"
            size="small"
            endIcon={<EyeIcon width={16} height={16} />}
            onClick={handleOpenClaimDetails}
            sx={{ flexShrink: 0, borderRadius: "999px", px: 2 }}
          >
            Claim Details
          </Button>
        </Box>
      </Box>
    );
  },
);

export default ClaimSummaryBar;
